/**
 * src/pages/jobwork/labelParser.js
 *
 * Same parser as the admin app's courier tracking
 * (admin-application src/pages/courierTracking/labelParser.js) — keep the two in step.
 * ─────────────────────────────────────────────────────────────────────────────
 * Turns the OCR text of a printed courier label into shipment form fields.
 *
 * The labels we stick on parcels look like:
 *
 *   To – Shambhavi
 *   Walmart Global TechCapitaLand
 *   International Tech Park (ITPC)
 *   200 Feet Radial Road, Zamin Pallavaram
 *   Chennai, Tamil Nadu 600117
 *   Phone: 9886521187
 *
 *   From – Marqland Studios
 *   …
 *
 * Only the "To" block is read; everything from the "From" line on is the
 * sender (us) and is ignored. State and city are matched against the shared
 * lists in utils/indiaLocations so they line up with the rest of the app; if
 * the label has no recognisable state, the PIN code's prefix is used instead.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { INDIA_STATES, CITIES_BY_STATE } from '../../utils/indiaLocations';

// Old / alternate names that still show up on labels → the name in our lists.
const CITY_ALIASES = {
  bangalore: 'Bengaluru', bengalooru: 'Bengaluru', mysore: 'Mysuru', mangalore: 'Mangaluru',
  hubli: 'Hubballi', bombay: 'Mumbai', madras: 'Chennai', calcutta: 'Kolkata',
  gurgaon: 'Gurugram', trichy: 'Tiruchirappalli', pondicherry: 'Puducherry',
  trivandrum: 'Thiruvananthapuram', cochin: 'Kochi', baroda: 'Vadodara', poona: 'Pune',
};
const STATE_ALIASES = {
  orissa: 'Odisha', pondicherry: 'Puducherry', 'new delhi': 'Delhi', uttaranchal: 'Uttarakhand',
  'tamilnadu': 'Tamil Nadu', 'j&k': 'Jammu and Kashmir',
};

// PIN code prefix → state, used only when the label text names no state.
// Three-digit entries override the two-digit postal-circle default.
const PIN_PREFIX_STATE = {
  11: 'Delhi', 12: 'Haryana', 13: 'Haryana', 14: 'Punjab', 15: 'Punjab', 16: 'Punjab',
  17: 'Himachal Pradesh', 18: 'Jammu and Kashmir', 19: 'Jammu and Kashmir',
  20: 'Uttar Pradesh', 21: 'Uttar Pradesh', 22: 'Uttar Pradesh', 23: 'Uttar Pradesh',
  24: 'Uttar Pradesh', 25: 'Uttar Pradesh', 26: 'Uttar Pradesh', 27: 'Uttar Pradesh', 28: 'Uttar Pradesh',
  30: 'Rajasthan', 31: 'Rajasthan', 32: 'Rajasthan', 33: 'Rajasthan', 34: 'Rajasthan',
  36: 'Gujarat', 37: 'Gujarat', 38: 'Gujarat', 39: 'Gujarat',
  40: 'Maharashtra', 41: 'Maharashtra', 42: 'Maharashtra', 43: 'Maharashtra', 44: 'Maharashtra',
  45: 'Madhya Pradesh', 46: 'Madhya Pradesh', 47: 'Madhya Pradesh', 48: 'Madhya Pradesh', 49: 'Chhattisgarh',
  50: 'Telangana', 51: 'Andhra Pradesh', 52: 'Andhra Pradesh', 53: 'Andhra Pradesh',
  56: 'Karnataka', 57: 'Karnataka', 58: 'Karnataka', 59: 'Karnataka',
  60: 'Tamil Nadu', 61: 'Tamil Nadu', 62: 'Tamil Nadu', 63: 'Tamil Nadu', 64: 'Tamil Nadu',
  67: 'Kerala', 68: 'Kerala', 69: 'Kerala',
  70: 'West Bengal', 71: 'West Bengal', 72: 'West Bengal', 73: 'West Bengal', 74: 'West Bengal',
  75: 'Odisha', 76: 'Odisha', 77: 'Odisha', 78: 'Assam',
  80: 'Bihar', 81: 'Bihar', 82: 'Jharkhand', 83: 'Jharkhand', 84: 'Bihar', 85: 'Bihar',
  160: 'Chandigarh', 194: 'Ladakh', 248: 'Uttarakhand', 249: 'Uttarakhand', 263: 'Uttarakhand',
  396: 'Dadra and Nagar Haveli and Daman and Diu', 403: 'Goa', 605: 'Puducherry',
  682: 'Kerala', 737: 'Sikkim', 744: 'Andaman and Nicobar Islands',
  790: 'Arunachal Pradesh', 791: 'Arunachal Pradesh', 792: 'Arunachal Pradesh',
  793: 'Meghalaya', 794: 'Meghalaya', 795: 'Manipur', 796: 'Mizoram', 797: 'Nagaland', 798: 'Nagaland', 799: 'Tripura',
};

/** Lowercase, letters/digits/& only, single spaces — so OCR punctuation noise doesn't break matches. */
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9&]+/g, ' ').trim();
/** Whole-word containment on normalised text. */
const hasWord = (haystack, needle) => ` ${norm(haystack)} `.includes(` ${norm(needle)} `);

/** Edit distance, for OCR-garbled names ("Tamil Nadn", "Maharashira"). */
const editDistance = (a, b) => {
  if (a === b) return 0;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
};

/**
 * Best fuzzy hit of any `names` inside `text`: each name is compared with every
 * run of the same number of words. Short names must match exactly; longer ones
 * may be off by about one character in six.
 */
const fuzzyFind = (text, names) => {
  const words = norm(text).split(' ').filter(Boolean);
  let best = null;
  for (const name of names) {
    const target = norm(name);
    const n = target.split(' ').length;
    const allowed = Math.floor(target.replace(/ /g, '').length / 6);
    if (!allowed) continue;
    for (let i = 0; i + n <= words.length; i++) {
      const d = editDistance(words.slice(i, i + n).join(' '), target);
      if (d <= allowed && (!best || d < best.d)) best = { name, d };
    }
  }
  return best?.name || '';
};

const stateFromPin = (pin) => {
  if (!pin) return '';
  return PIN_PREFIX_STATE[pin.slice(0, 3)] || PIN_PREFIX_STATE[pin.slice(0, 2)] || '';
};

// Longest names first so "West Bengal" wins over a stray "Bengal", etc.
const STATES_LONGEST_FIRST = [...INDIA_STATES].sort((a, b) => b.length - a.length);

const findStateExact = (text) => {
  const direct = STATES_LONGEST_FIRST.find((st) => hasWord(text, st));
  if (direct) return direct;
  const alias = Object.keys(STATE_ALIASES).find((a) => hasWord(text, a));
  return alias ? STATE_ALIASES[alias] : '';
};

const cityLabel = (c) => c.replace(/\s*\(.*\)\s*$/, '');

const findCity = (text, state, { fuzzy = false } = {}) => {
  const pool = state && CITIES_BY_STATE[state]
    ? CITIES_BY_STATE[state]
    : Object.values(CITIES_BY_STATE).flat();
  const byLength = [...new Set(pool)].sort((a, b) => b.length - a.length);
  const direct = byLength.find((c) => hasWord(text, cityLabel(c)));
  if (direct) return direct;
  const alias = Object.keys(CITY_ALIASES).find((a) => hasWord(text, a));
  if (alias) return CITY_ALIASES[alias];
  if (!fuzzy) return '';
  const hit = fuzzyFind(text, byLength.map(cityLabel));
  return hit ? byLength.find((c) => cityLabel(c) === hit) : '';
};

const stateOfCity = (city) =>
  Object.keys(CITIES_BY_STATE).find((st) => CITIES_BY_STATE[st].includes(city)) || '';

// Indian mobile: optional +91 / 0, then 10 digits starting 6-9 (spaces or dashes allowed).
const PHONE_RE = /(?:\+?\s*9\s?1[\s-]*|\b0)?([6-9](?:[\s-]?\d){9})(?![\s-]?\d)/;
const PHONE_LABEL_RE = /\b(ph(?:one)?|mob(?:ile)?|tel|cell|contact|m)\b\.?/i;
const PIN_RE = /(?<!\d)([1-9]\d{2})\s?(\d{3})(?!\s?\d)/g;
const PIN_LABEL_RE = /\b(pin(?:\s*code)?|pincode|postal\s*code)\b/i;
const TO_RE = /^\W*(?:deliver(?:y)?\s+t[o0p]|ship(?:ping)?\s+t[o0p]|consignee|t[o0])\b[\s.:;,\-–—]*(.*)$/i;
// Every parcel is sent by Marqland Studios, so the studio's name (even OCR-mangled:
// "Margland", "Marqlang") marks the sender block when the "From" word itself is lost.
const FROM_RE = /^\W*(?:fr[o0]m|sender|return\s+to)\b|\bm[ae]r[qg]\s?l[ae]n[dg]\b/i;

/**
 * OCR confuses letters and digits inside numbers (O↔0, l/I/|↔1, S↔5, B↔8).
 * Repair them only inside number-like tokens, so names and streets are untouched.
 */
const fixDigits = (line) => line.replace(/[0-9OoIlL|!SsBZ][0-9OoIlL|!SsBZ\s-]*[0-9OoIlL|!SsBZ]/g, (tok) => {
  const digits = (tok.match(/\d/g) || []).length;
  const letters = (tok.match(/[A-Za-z|!]/g) || []).length;
  if (digits < 4 || letters > 2) return tok;
  return tok.replace(/[Oo]/g, '0').replace(/[IlL|!]/g, '1').replace(/[Ss]/g, '5').replace(/B/g, '8').replace(/Z/g, '2');
});

/** Strip OCR edge junk (stray quotes, bars, bullets) and collapse spaces. */
const cleanLine = (l) => String(l || '').replace(/\s+/g, ' ').replace(/^[\s|'"`‘’“”•*_~]+|[\s|'"`‘’“”•*_~]+$/g, '').trim();
const isText = (l) => (l.match(/[a-z0-9]/gi) || []).length >= 2;
/**
 * Texture on cardboard, tape and shadows comes out of OCR as rows of 1-3 letter
 * fragments ("ee a | SE : | Ca AR AI"). A real label line has a word of 3+
 * letters or a number of 3+ digits, and isn't mostly fragments.
 */
const isJunk = (l) => {
  if (TO_RE.test(l) || FROM_RE.test(l)) return false;
  const tokens = l.split(' ').filter(Boolean);
  const real = tokens.filter((t) => /[a-z]{3,}/i.test(t) || /\d{3,}/.test(t));
  if (!real.length) return true;
  const fragments = tokens.filter((t) => !/[a-z]{3,}/i.test(t) && !/\d{2,}/.test(t));
  return tokens.length >= 4 && fragments.length / tokens.length > 0.6;
};
const cleanName = (s) => cleanLine(s).replace(/^[\s,.:;\-–—]+|[\s,.:;\-–—]+$/g, '');

const EMPTY = () => ({ recipientName: '', recipientAddress: '', city: '', state: '', phone: '', pincode: '' });

/** Pick the recipient ("To") block out of all the label lines. */
const recipientBlock = (lines) => {
  const toIdx = lines.findIndex((l) => TO_RE.test(l));
  if (toIdx >= 0) {
    const fromAfter = lines.findIndex((l, i) => i > toIdx && FROM_RE.test(l));
    return { block: lines.slice(toIdx, fromAfter > 0 ? fromAfter : lines.length), hasTo: true };
  }
  const fromIdx = lines.findIndex((l) => FROM_RE.test(l));
  if (fromIdx > 0) return { block: lines.slice(0, fromIdx), hasTo: false };
  if (fromIdx === 0) {
    // Sender printed first and no "To" line: the sender block ends at its phone (or PIN) line.
    const endIdx = lines.findIndex((l, i) => i > 0 && PHONE_RE.test(fixDigits(l)));
    const pinIdx = lines.findIndex((l, i) => i > 0 && new RegExp(PIN_RE.source).test(fixDigits(l)));
    const cut = endIdx > 0 ? endIdx : pinIdx;
    return { block: cut > 0 ? lines.slice(cut + 1) : lines.slice(1), hasTo: false };
  }
  return { block: lines, hasTo: false };
};

function parseOne(rawText, lineConfs, sender = { phones: new Set(), pins: new Set() }) {
  const result = EMPTY();
  let lines = String(rawText || '').split(/\r?\n/);
  // With per-line OCR confidence, drop lines Tesseract was guessing at (tape, barcodes, box print).
  if (Array.isArray(lineConfs) && lineConfs.length === lines.length) lines = lines.filter((_, i) => lineConfs[i] >= 35);
  lines = lines.map(cleanLine).filter((l) => isText(l) && !isJunk(l));
  if (!lines.length) return result;

  const { block, hasTo } = recipientBlock(lines);
  if (!block.length) return result;

  // Recipient name: text after "To –", or the next line if "To" stands alone.
  let start = 0;
  if (hasTo) {
    const rest = cleanName(block[0].match(TO_RE)[1]);
    if (/[a-z]{2}/i.test(rest)) { result.recipientName = rest; start = 1; } else if (block[1]) {
      result.recipientName = cleanName(block[1]); start = 2;
    } else start = 1;
  } else {
    result.recipientName = cleanName(block[0]); start = 1;
  }
  // Lines carrying the sender's phone (seen in the "From" block of any read of this
  // photo) belong to the sender even when this read lost the "From" line. Same for
  // the sender's PIN line — unless it's the only PIN, as we do ship to our own area.
  const pinsOf = (l) => [...fixDigits(l).matchAll(new RegExp(PIN_RE.source, 'g'))].map((m) => m[1] + m[2]);
  const isSenderPhone = (l) => sender.phones.has(fixDigits(l).match(PHONE_RE)?.[1]?.replace(/\D/g, '') || '');
  // A bare "Phone:" whose number OCR lost is no address line either.
  let body = block.slice(start).filter((l) => !isSenderPhone(l) && !/^\W*(?:ph(?:one)?|mob(?:ile)?|tel|cell|contact)\W*$/i.test(l));
  const hasOtherPin = body.some((l) => pinsOf(l).some((p) => !sender.pins.has(p)));
  if (hasOtherPin) body = body.filter((l) => { const pins = pinsOf(l); return !pins.length || !pins.every((p) => sender.pins.has(p)); });

  // Phone: prefer a line labelled Phone/Mob, else any line holding a 10-digit number.
  const fixed = body.map(fixDigits);
  const phoneIdx = (() => {
    const labelled = fixed.findIndex((l) => PHONE_LABEL_RE.test(l) && PHONE_RE.test(l));
    return labelled >= 0 ? labelled : fixed.findIndex((l) => PHONE_RE.test(l));
  })();
  if (phoneIdx >= 0) {
    result.phone = fixed[phoneIdx].match(PHONE_RE)[1].replace(/\D/g, '');
    const leftover = fixed[phoneIdx].replace(PHONE_RE, '').replace(PHONE_LABEL_RE, '').replace(/[\s:.\-–—]+/g, ' ').trim();
    body = body.filter((_, i) => i !== phoneIdx);
    if (/[a-z]{3,}/i.test(leftover)) body.push(leftover);
  }

  // PIN: a "PIN:" line wins; otherwise the last six-digit group in the block.
  let pinLine = -1;
  const scanPins = (onlyLabelled) => body.forEach((l, i) => {
    const f = fixDigits(l);
    if (onlyLabelled && !PIN_LABEL_RE.test(f)) return;
    const re = new RegExp(PIN_RE.source, 'g');
    let m;
    while ((m = re.exec(f))) { result.pincode = m[1] + m[2]; pinLine = i; }
  });
  scanPins(true);
  if (pinLine < 0) scanPins(false);

  // State + city: look at the PIN line first (that's where they normally sit), then the rest.
  const ordered = pinLine >= 0 ? [body[pinLine], ...body.filter((_, i) => i !== pinLine)] : [...body].reverse();
  const pinState = stateFromPin(result.pincode);
  for (const l of ordered) { result.state = findStateExact(l); if (result.state) break; }
  if (!result.state) {
    let fuzzy = '';
    for (const l of ordered) { fuzzy = fuzzyFind(l, INDIA_STATES); if (fuzzy) break; }
    // A garbled state name is trusted when the PIN agrees with it (or there's no PIN).
    result.state = fuzzy && (!pinState || fuzzy === pinState) ? fuzzy : pinState || fuzzy;
  }
  for (const l of ordered) { result.city = findCity(l, result.state); if (result.city) break; }
  if (!result.city) for (const l of ordered) { result.city = findCity(l, result.state, { fuzzy: true }); if (result.city) break; }
  if (!result.city && result.state) {
    // City might be one we don't list for that state — fall back to any known city.
    for (const l of ordered) { result.city = findCity(l, ''); if (result.city) break; }
  }
  if (result.city && !result.state) result.state = stateOfCity(result.city);

  // Address: every remaining line, with the city/state/PIN line reduced to what's
  // left after removing those (the PIN is kept so it isn't lost — there's no PIN field).
  const addressParts = body.map((l, i) => {
    let rest = l;
    if (i === pinLine) rest = fixDigits(rest).replace(new RegExp(PIN_RE.source, 'g'), ' ').replace(PIN_LABEL_RE, ' ');
    const looksLikeCityLine = i === pinLine || [result.city, result.state].some((v) => v && hasWord(l, v));
    if (!looksLikeCityLine) return l;
    [result.city && cityLabel(result.city), result.state].filter(Boolean).forEach((v) => {
      rest = rest.replace(new RegExp(`\\b${v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i'), ' ');
    });
    rest = rest.replace(/[\s,.:\-–—]+/g, ' ').trim();
    return i === pinLine ? [rest, result.pincode].filter(Boolean).join(' - ') : rest;
  }).filter(Boolean);
  result.recipientAddress = addressParts.join(', ').replace(/,\s*,/g, ',').replace(/\s+,/g, ',');

  return result;
}

/** How complete and self-consistent a parse is — used to choose between OCR reads. */
const parseScore = (r) => (r.recipientName ? 3 : 0) + (r.phone ? 3 : 0) + (r.pincode ? 2 : 0)
  + (r.state ? 1 : 0) + (r.city ? 1 : 0) + (r.recipientAddress.length > 8 ? 1 : 0)
  + (r.pincode && r.state && stateFromPin(r.pincode) === r.state ? 2 : 0);

/**
 * Parse OCR text from a label.
 * @param {string | Array<string | { text: string, lines?: Array<{ conf: number }> }>} input
 *   one OCR read, or several reads of the same photo (as /shipments/scan-label
 *   returns them) — the most complete parse wins and its gaps are filled from the others.
 * @returns {{ recipientName, recipientAddress, city, state, phone, pincode }}
 *          Fields that couldn't be read come back as ''.
 */
/** Phones and PINs printed in the sender ("From") block of a read. */
const senderDetails = (rawText, sender) => {
  const lines = String(rawText || '').split(/\r?\n/).map(cleanLine).filter(isText);
  const toIdx = lines.findIndex((l) => TO_RE.test(l));
  lines.forEach((l, i) => {
    if (!FROM_RE.test(l)) return;
    // The sender block runs to the "To" line (if it comes later) or the end.
    const end = toIdx > i ? toIdx : lines.length;
    for (const sl of lines.slice(i, Math.min(end, i + 4))) {
      const f = fixDigits(sl);
      const phone = f.match(PHONE_RE)?.[1];
      if (phone) sender.phones.add(phone.replace(/\D/g, ''));
      for (const m of f.matchAll(new RegExp(PIN_RE.source, 'g'))) sender.pins.add(m[1] + m[2]);
    }
  });
  return sender;
};

export function parseLabelText(input) {
  const reads = (Array.isArray(input) ? input : [input])
    .map((c) => (typeof c === 'string' || c == null ? { text: c } : c));
  const sender = { phones: new Set(), pins: new Set() };
  reads.forEach((r) => senderDetails(r.text, sender));
  // Most complete parse first; on a tie, the read OCR was more confident about.
  const parses = reads
    .map((r) => ({ ...parseOne(r.text, r.lines?.map((l) => l.conf), sender), _conf: r.confidence || 0 }))
    .sort((a, b) => parseScore(b) - parseScore(a) || b._conf - a._conf)
    .map(({ _conf, ...r }) => r);
  if (!parses.length) return EMPTY();
  const [best, ...rest] = parses;
  const merged = { ...best };
  for (const k of Object.keys(merged)) {
    if (!merged[k]) merged[k] = rest.find((r) => r[k])?.[k] || '';
  }
  // City/state filled from different reads must still agree.
  if (merged.city && merged.state && !best.city && !(CITIES_BY_STATE[merged.state] || []).includes(merged.city)) merged.city = '';
  return merged;
}

export default parseLabelText;
